import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import Animator from 'src/lib/Animator';
import { animDuration, parseAmount, waitForItPromise } from 'src/lib/utils';
import { ProgressBar } from '../components/shared/ProgressBar';
import { animateCoins, animateGems } from '../animation/CurrencyAnimation';
import { duration } from 'src/replicant/utils/duration';
import { Match3 } from 'src/replicant/minigame/match3';

type PopupMinigameResultsArgs = { points: number; coins: number; gems: number };

const milestones = Match3.ruleset.rewardMilestones;

const style = uiConfig.minigames.result;

export default class PopupMinigameResults extends PopupBasic {
  private pointsAnimator: Animator;
  private coinsAnimator: Animator;
  private gemsAnimator: Animator;
  private progressBar: ProgressBar;
  private args: PopupMinigameResultsArgs;
  private gemIcon: ImageScaleView;
  private coinIcon: ImageScaleView;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 780,
      darkerBg: true,
      skipMessage: true,
      titleIcon: { image: 'assets/ui/minigames/common/result_icon.png' },
      tracking: {
        feature: 'minigames',
        $subFeature: 'popup-minigames-results',
      },
    });

    this.buttonClose.removeFromSuperview();

    const flexBox = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height - 95,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 100,
      },
    });

    const scoreSection = new FlexBoxView({
      superview: flexBox,
      width: flexBox.style.width,
      height: 100,
      flex: {
        justifyContent: 'space-between',
        paddingHorizontal: 30,
      },
    });

    new LangBitmapFontTextView({
      superview: scoreSection,
      ...style.titleText,
      localeText: () => i18n('minigames.score'),
    });

    const frame = new ImageScaleView({
      superview: scoreSection,
      ...style.scoreFrame,
    });

    const points = new LangBitmapFontTextView({
      superview: frame,
      ...style.points,
      localeText: () => '0 pt',
    });

    this.pointsAnimator = new Animator((value) => {
      points.localeText = () =>
        i18n('minigames.points', { score: parseAmount(value) });
    });

    new View({
      superview: flexBox,
      ...style.separator,
      width: this.box.style.width - 10,
    });

    const progressContainer = new FlexBoxView({
      superview: flexBox,
      width: flexBox.style.width,
      height: 250,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.progressBar = new ProgressBar({
      superview: progressContainer,
      domain: milestones.map(({ points }) => points),
    });

    for (let i = 0; i < milestones.length; i++) {
      this.createProgressBarMilestone(i);
    }

    new View({
      superview: flexBox,
      height: 5,
      width: this.box.style.width - 10,
      backgroundColor: '#e7b77e',
    });

    const rewards = new LangBitmapFontTextView({
      superview: flexBox,
      ...style.titleText,
      localeText: () => i18n('minigames.reward'),
    });

    getFlexNode(rewards).updateStyle({
      alignSelf: 'flex-start',
      marginLeft: 30,
      marginTop: 15,
      marginBottom: 20,
    });

    const rewardsSection = new FlexBoxView({
      superview: flexBox,
      width: flexBox.style.width,
      height: 76,
      flex: {
        justifyContent: 'space-between',
        paddingHorizontal: 30,
      },
    });

    const coinsFrame = new ImageScaleView({
      superview: rewardsSection,
      ...style.coinsFrame,
    });

    this.coinIcon = new ImageScaleView({
      superview: coinsFrame,
      ...style.coinIcon,
      y: coinsFrame.style.height * 0.5,
    });

    const coinsText = new LangBitmapFontTextView({
      superview: coinsFrame,
      ...style.valueLabel,
      localeText: () => 'X 0',
    });

    this.coinsAnimator = new Animator((value) => {
      coinsText.localeText = () => `X ${parseAmount(value)}`;
    });

    const gemsFrame = new ImageScaleView({
      superview: rewardsSection,
      ...style.coinsFrame,
      width: 213,
    });

    this.gemIcon = new ImageScaleView({
      superview: gemsFrame,
      ...style.gemIcon,
      y: coinsFrame.style.height * 0.5,
    });

    const gemsText = new LangBitmapFontTextView({
      superview: gemsFrame,
      ...style.valueLabel,
      localeText: () => 'X 0',
      width: 190,
    });

    this.gemsAnimator = new Animator((value) => {
      gemsText.localeText = () => `X ${parseAmount(value)}`;
    });

    new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 75,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      onClick: this.onButtonClick.bind(this),
      tracking: {
        feature: 'minigames',
        $subFeature: 'popup-minigames-results-close',
      },
    });
  }

  public init(args: PopupMinigameResultsArgs) {
    super.init({
      title: i18n('minigames.result'),
    });

    this.args = {
      points: 3400,
      coins: 300,
      gems: 99,
      ...(args ?? {}),
    };
  }

  public async fadeIn() {
    super.fadeIn();

    await waitForItPromise(3 * animDuration);

    const { points, coins, gems } = this.args;

    await this.pointsAnimator.setTarget(points);
    await this.progressBar.animateProgress(points, duration({ seconds: 2 }));
    await Promise.all([
      this.coinsAnimator.setTarget(coins),
      this.gemsAnimator.setTarget(gems),
    ]);
  }

  private async onButtonClick() {
    sounds.sfx.play('ok');

    // Make coins fly to the top of the screen.
    const coinIconPos = this.coinIcon.getPosition(this.root);
    const coinsStart = {
      x: coinIconPos.x + this.coinIcon.style.width / 2,
      y: coinIconPos.y + this.coinIcon.style.height / 2,
    };
    const coinsAnimation = animateCoins({
      superview: this.root,
      start: { position: coinsStart, scale: 1 },
      count: this.args.coins,
    });

    // Make gems fly to the top of the screen.
    const gemIconPos = this.gemIcon.getPosition(this.root);
    const gemsStart = {
      x: gemIconPos.x + this.gemIcon.style.width / 2,
      y: gemIconPos.y + this.gemIcon.style.height / 2,
    };
    const gemsAnimation = animateGems({
      superview: this.root,
      start: { position: gemsStart, scale: 1 },
      count: this.args.gems,
    });

    // Wait for both animations to finish.
    await Promise.all([coinsAnimation, gemsAnimation]);

    return this.opts.close();
  }

  private createProgressBarMilestone(index: number) {
    const padding = 30;
    const x =
      padding +
      index *
        ((this.progressBar.style.width - 2 * padding) /
          (milestones.length - 1));

    const upperArrow = new ImageScaleView({
      superview: this.progressBar,
      ...style.arrowDown,
      x: x + 1,
    });

    new LangBitmapFontTextView({
      superview: upperArrow,
      ...style.arrowLabel,
      localeText: () =>
        i18n('minigames.points', {
          score: parseAmount(milestones[index].points),
        }),
    });

    new View({
      superview: this.progressBar,
      width: 3,
      height: this.progressBar.style.height - 5,
      backgroundColor: '#a6754e',
      x,
      visible: index !== 0 && index !== milestones.length - 1,
    });

    const lowerArrow = new ImageScaleView({
      superview: this.progressBar,
      ...style.arrowDown,
      x: x + 1,
      y: this.progressBar.style.height + 5,
      r: Math.PI,
    });

    new LangBitmapFontTextView({
      superview: lowerArrow,
      ...style.arrowLabel,
      localeText: () => `${parseAmount(milestones[index].coins)}`,
      r: -Math.PI,
    });

    new ImageScaleView({
      superview: lowerArrow,
      ...style.arrowCoinIcon,
      r: -Math.PI,
    });
  }
}
